import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Link from 'next/link';
import AccountQuickLinksMobile from './AccountQuickLinksMobile';
import useEcomerce from '~/hooks/useEcomerce';
import { calculateAmount } from '~/utilities/ecomerce-helpers';
import useGetProducts from '~/hooks/useGetProducts';

const MobileHeaderActions = ({ auth }) => {
  const dispatch = useDispatch();
  const { removeItem } = useEcomerce();
  const cartItems = useSelector(({ ecomerce }) => ecomerce.cartItems);
  
  const reduxIsLoggedIn = useSelector(({ user }) => user.isLoggedIn);
  const { getStrapiProducts, products } = useGetProducts();

  const [pulseCartCount, setPulseCartCount] = useState(false);
  
  const [localAuthState, setLocalAuthState] = useState('loading');
  const [isAccountOpen, setIsAccountOpen] = useState(false);

  const prevCartTotalRef = useRef(0);
  const isInitialMountRef = useRef(true);
  const mountedRef = useRef(false);

  useEffect(() => {
    const token = localStorage.getItem('jwt');
    
    if (token) {
      setLocalAuthState('loggedIn');
    } else {
      setLocalAuthState('notLoggedIn');
    }
  }, []);

  useEffect(() => {
    if (localAuthState !== 'loading' && reduxIsLoggedIn !== undefined) {
    }
  }, [localAuthState, reduxIsLoggedIn]);

  function handleRemoveItem(e, productId) {
    e.preventDefault();
    removeItem({ id: productId }, cartItems, 'cart');
  }

  function getCartProducts() {
    if (cartItems.length > 0) {
      const query = {
        filters: {
          id: {
            $in: cartItems.map((item) => item.id),
          },
        },
        populate: { shippings: { fields: ['shipping_type', 'shipping_cost'] } },
      };
      getStrapiProducts(query);
    }
  }

  useEffect(() => {
    getCartProducts();
  }, [cartItems]);

  const totalQuantity = useMemo(() => {
    return cartItems.reduce((total, item) => total + (item.quantity || 1), 0);
  }, [cartItems]);

  const triggerPulseIfAdded = useCallback(() => {
    const currentTotal = totalQuantity;
    const prevTotal = prevCartTotalRef.current;

    if (mountedRef.current && !isInitialMountRef.current && currentTotal > prevTotal) {
      setPulseCartCount(true);
      setTimeout(() => setPulseCartCount(false), 500);
    }

    prevCartTotalRef.current = currentTotal;
  }, [totalQuantity]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevCartTotalRef.current = totalQuantity;
      return;
    }
    triggerPulseIfAdded();
  }, [totalQuantity, triggerPulseIfAdded]);

  useEffect(() => {
    if (!isAccountOpen || localAuthState !== 'loggedIn') return;

    const onDocMouseDown = (e) => {
      const accountRoot = document.querySelector('.account-click-root');
      if (accountRoot && !accountRoot.contains(e.target)) {
        setIsAccountOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isAccountOpen, localAuthState]);

  const onAccountToggle = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAccountOpen((v) => !v);
  }, []);

  const cartProducts = useMemo(() => {
    if (cartItems.length === 0) return [];
    return products.map((product) => ({
      id: product.id,
      title: product.attributes.title || 'Untitled Product',
      slug: product.attributes.slug || 'untitled-product',
      thumbnailImage: product.attributes.thumbnail || null,
      price: product.attributes.price || 0,
      base_image_thumbnail: product.attributes.base_image_thumbnail || 'No base_image_thumbnail',
      quantity: cartItems.find((item) => item.id === product.id)?.quantity ?? 0,
      variant_name_1: product?.attributes.variant_name_1,
      variant_value_1: product?.attributes.variant_value_1,
      variant_name_2: product?.attributes.variant_name_2,
      variant_value_2: product?.attributes.variant_value_2,
      variant_name_3: product?.attributes.variant_name_3,
      variant_value_3: product?.attributes.variant_value_3,
      variant_name_4: product?.attributes.variant_name_4,
      variant_value_4: product?.attributes.variant_value_4,
    }));
  }, [products, cartItems]);

  const cartAmount = useMemo(() => {
    return calculateAmount(cartProducts);
  }, [cartProducts]);

  if (localAuthState === 'loading') {
    return (
      <div className="navigation__right" style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', marginLeft: '20px' }}>
        <div style={{ visibility: 'hidden' }}>
          <div className="ps-block--user-account">
            <div className="ps-block__header">
              <i className="icon-bag2" />
              <span>Warenkorb</span>
            </div>
          </div>
        </div>
        <div className="account-skeleton" />
      </div>
    );
  }

  return (
    <div className="navigation__right" style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', marginLeft: '20px' }}>
      <Link href="/account/shopping-cart" className="cart-link">
        <div className={`ps-block--user-account cart-no-dropdown ${pulseCartCount ? 'pulse' : ''}`}>
          <div className="ps-block__header">
            <i className="icon-bag2" data-count={totalQuantity > 0 ? totalQuantity : '0'} />
            <span className="account-text">Warenkorb</span>
          </div>
        </div>
      </Link>

      <div className="account-click-root">
        {localAuthState === 'loggedIn' ? (
          <AccountQuickLinksMobile
            auth={auth}
            isOpen={isAccountOpen}
            onToggle={onAccountToggle}
          />
        ) : (
          <Link href="/account/login" className="ps-block--user-account account-not-logged-in">
            <div className="ps-block__header">
              <i className="icon-user" />
              <span className="account-text">Anmelden</span>
            </div>
          </Link>
        )}
      </div>

      <style jsx>{`
        .account-skeleton {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: loading 1.5s infinite;
        }
    
        @keyframes loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .cart-link { text-decoration: none !important; display: block; }
        .ps-block--user-account { margin-top: 12px; cursor: pointer; }
        .ps-block__header { 
          display: flex; flex-direction: column; align-items: center; 
        }
        .ps-block__header i {
          font-size: 24px; color: #6c757d; width: 40px; height: 40px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 50%; background: #f8f9fa; border: 2px solid #e9ecef;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); position: relative;
          transition: all 0.3s ease;
        }
        .cart-link:hover .ps-block__header i {
          background: #fff; border-color: #2d4670; color: #2d4670; transform: scale(1.05);
        }
        .ps-block__header i[data-count]:after {
          content: attr(data-count); position: absolute; top: -8px; right: -8px;
          min-width: 22px; height: 22px; border-radius: 50%; background: #bf0000 !important;
          color: white !important; font-weight: 700 !important; font-size: 11px !important;
          display: flex !important; align-items: center !important; justify-content: center !important;
          line-height: 1 !important; box-shadow: 0 2px 8px rgba(255, 107, 107, 0.4);
          border: 2px solid white; font-family: inherit !important;
        }
        .account-text {
          margin-top: 6px; font-weight: 600; color: #333; font-size: 13px; letter-spacing: 0.3px;
        }
        .account-not-logged-in {
          display: flex; flex-direction: column; align-items: center; padding: 8px;
          border-radius: 8px; transition: all 0.2s ease; text-decoration: none !important;
        }
        .account-not-logged-in:hover { background: #f8f9fa; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); }
        .account-not-logged-in .ps-block__header i {
          font-size: 24px; color: #6c757d; width: 40px; height: 40px; display: flex;
          align-items: center; justify-content: center; border-radius: 50%;
          background: #f8f9fa; border: 2px solid #e9ecef; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        .account-not-logged-in:hover .ps-block__header i {
          background: #fff; border-color: #2d4670; color: #2d4670; transform: scale(1.05);
        }
      `}</style>

      <style jsx global>{`
        .cart-no-dropdown .ps-dropdown-wrapper,
        .cart-no-dropdown .hover-bridge,
        .cart-no-dropdown .ps-block__content { display: none !important; }
        .account-click-root .ps-block--user-account:hover .ps-dropdown-wrapper {
          pointer-events: none !important;
        }
      `}</style>
    </div>
  );
};

export default MobileHeaderActions;